import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getGroupedRowModel,
    getSortedRowModel,
    Row,
    RowSelectionState,
    TableMeta,
    ColumnSort as TanstackColumnSort,
    Updater,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';
import { Fragment, ReactNode, useMemo } from 'react';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { Container } from '../Container';
import { EmptyState, EmptyStateProps } from '../EmptyState';
import { LoadingOverlay } from '../helpers/LoadingOverlay';
import { IconButton } from '../IconButton';
import { Spinner } from '../Spinner';
import { Typography } from '../typography';
import { TableColumnHeaderActions, TablePagination } from './components';
import { useTableColumns } from './hooks';
import { PrintStyles } from './styles/print-style';
import {
    TableBody,
    TableBodyRow,
    TableCell,
    TableCellInner,
    TableContainer,
    TableElement,
    TableHeader,
    TableHeaderCell,
    TableHeaderRow,
    TableWrapper,
} from './styles/style';
import { TableProvider, useTableContext } from './TableContext';
import { ColumnSort, Order, TableFilters, TableState } from './types';

export interface TableProps<T> {
    data: T[];
    uniqueRowId: keyof T;
    loading?: boolean;
    amountOfRows: number;
    fixedFirstColumn?: boolean;
    fixedLastColumn?: boolean;
    fixedHeader?: boolean;
    rowSelection?: {
        rowSelection: RowSelectionState;
        onRowSelectionChange: (updater: Updater<RowSelectionState>) => void;
        enableRowSelection?: boolean | ((row: Row<T>) => boolean);
    };
    manualFiltering?: boolean;
    manualSorting?: boolean;
    meta?: TableMeta<T>;
    emptyState?: EmptyStateProps;
    columnVisibility?: VisibilityState;
    getRowSubComponent?: (row: Row<T>) => ReactNode | undefined;
    getRowGroupComponent?: (row: Row<T>) => ReactNode | undefined;
    getRowGroupLabel?: (row: Row<T>) => React.ReactNode | undefined;
    groupedRowColSpan?: number;
}

export const Table = <T, F extends TableFilters<T> = TableFilters<T>>({
    fixedFirstColumn = false,
    fixedLastColumn = false,
    fixedHeader = false,
    amountOfRows,
    data,
    loading: isLoading,
    rowSelection: rowSelectionObject,
    meta,
    uniqueRowId,
    emptyState,
    manualFiltering = false,
    manualSorting = false,
    columnVisibility,
    getRowSubComponent,
    getRowGroupComponent,
    getRowGroupLabel,
    groupedRowColSpan,
    ...other
}: TableProps<T>) => {
    const { rowSelection, onRowSelectionChange, enableRowSelection } = rowSelectionObject || {};
    const rowSelectionEnabled = !!rowSelectionObject && enableRowSelection !== false;
    const { state, onStateChange } = useTableContext<T, F>();
    const isGroupingEnabled = state.grouping && state.grouping.length > 0;

    const sorting: TanstackColumnSort[] = useMemo(
        () =>
            Object.entries<ColumnSort | undefined>(state.sort).map(([key, colmnSort]) => ({
                id: key,
                desc: colmnSort?.order === Order.Descending,
            })),
        [state.sort]
    );

    const table = useReactTable({
        columns: useTableColumns<T>({ enableRowSelection: rowSelectionEnabled }),
        data,
        defaultColumn: {
            enableSorting: false,
            enableColumnFilter: false,
        },
        getCoreRowModel: getCoreRowModel(),
        getGroupedRowModel: isGroupingEnabled ? getGroupedRowModel() : undefined,
        getSortedRowModel: getSortedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: getRowSubComponent ? () => true : undefined,
        enableRowSelection,
        getRowId: (row) => row[uniqueRowId] as string,
        manualFiltering,
        manualSorting,
        meta,
        onRowSelectionChange,
        state: {
            rowSelection,
            columnVisibility,
            sorting,
            expanded: state.expanded,
            grouping: state.grouping,
        },
        onExpandedChange: (updaterOrValue) =>
            onStateChange({
                ...state,
                expanded: typeof updaterOrValue === 'function' ? updaterOrValue(state.expanded) : updaterOrValue,
            }),
        onGroupingChange: (newGrouping) =>
            onStateChange({
                ...state,
                grouping: typeof newGrouping === 'function' ? newGrouping(state.grouping || []) : newGrouping,
            }),
    });

    const { t } = useLocalization();

    const pageCount = Math.ceil(amountOfRows / state.pagination.pageSize) ?? -1;
    const hasData = data.length > 0;

    const renderEmpty = () => {
        if (hasData) {
            return null;
        }
        return !isLoading ? (
            <EmptyState
                {...emptyState}
                primaryText={emptyState?.primaryText ?? t(OptimusTextKey.TableEmptyStatePrimary)}
            />
        ) : (
            <Container padding={['900', '0']} />
        );
    };

    const displayRowSubComponentIfRowIsExpandable = (row: Row<T>) => {
        if (getRowSubComponent && row.getCanExpand() && row.getIsExpanded()) {
            const component = getRowSubComponent(row);
            if (component) {
                return (
                    <tr>
                        <td colSpan={row.getVisibleCells().length}>{component}</td>
                    </tr>
                );
            }
        }
        return null;
    };

    return (
        <TableWrapper aria-disabled={isLoading} {...other}>
            <TableContainer>
                <TableElement>
                    <TableHeader fixedHeader={fixedHeader}>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableHeaderRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const sortable = header.column.getCanSort();
                                    const filterable = header.column.getCanFilter();

                                    return (
                                        <TableHeaderCell
                                            key={header.id}
                                            fixedFirstColumn={fixedFirstColumn}
                                            fixedLastColumn={fixedLastColumn}
                                            fixedHeader={fixedHeader}
                                            rowSelectionEnabled={rowSelectionEnabled}
                                            width={header.column.columnDef.meta?.width}
                                            interactive={sortable || filterable}
                                            filterable={filterable}
                                            data-test-column-id={header.column.id}
                                        >
                                            <TableColumnHeaderActions
                                                header={header}
                                                align={header.column.columnDef.meta?.align}
                                                setRowSelection={onRowSelectionChange}
                                                sortable={sortable}
                                                filterable={filterable}
                                            />
                                        </TableHeaderCell>
                                    );
                                })}
                            </TableHeaderRow>
                        ))}
                    </TableHeader>
                    <TableBody isLoading={isLoading}>
                        {table.getRowModel().rows.map((row, index, rows) => (
                            <Fragment key={row.id}>
                                {row.getIsGrouped() ? (
                                    <TableBodyRow>
                                        <TableCell
                                            colSpan={groupedRowColSpan || row.getVisibleCells().length - 1}
                                            fixedLastColumn={false}
                                            rowSelectionEnabled={false}
                                            fixedFirstColumn={false}
                                        >
                                            <TableCellInner>
                                                <IconButton
                                                    onClick={row.getToggleExpandedHandler()}
                                                    type="button"
                                                    icon={
                                                        row.getIsExpanded()
                                                            ? IconName.ChevronDown
                                                            : IconName.ChevronRight
                                                    }
                                                    variant="ghost"
                                                />
                                                <Typography textStyle="bodyMediumStrong" as="div">
                                                    {getRowGroupLabel ? getRowGroupLabel(row) : row.groupingValue}
                                                </Typography>
                                            </TableCellInner>
                                        </TableCell>
                                        {getRowGroupComponent?.(row)}
                                    </TableBodyRow>
                                ) : (
                                    <TableBodyRow
                                        active={rowSelection && row.id in rowSelection}
                                        nextIsActive={rowSelection && rows[index + 1]?.id in rowSelection}
                                        previousIsActive={rowSelection && rows[index - 1]?.id in rowSelection}
                                        data-test-table-row={row.id}
                                    >
                                        {row.getVisibleCells().map((cell, cellIndex) => {
                                            const renderedContent = flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            );
                                            const isFirst = cellIndex === 0;

                                            return (
                                                <TableCell
                                                    key={cell.id}
                                                    data-test-table-cell={cell.id}
                                                    rowSelectionEnabled={rowSelectionEnabled}
                                                    fixedFirstColumn={fixedFirstColumn}
                                                    fixedLastColumn={fixedLastColumn}
                                                >
                                                    <TableCellInner align={cell.column.columnDef.meta?.align}>
                                                        {row.depth !== 0 && isFirst ? (
                                                            <Container padding={['0', '0', '0', '500']}>
                                                                {renderedContent}
                                                            </Container>
                                                        ) : (
                                                            renderedContent
                                                        )}
                                                    </TableCellInner>
                                                </TableCell>
                                            );
                                        })}
                                    </TableBodyRow>
                                )}
                                {displayRowSubComponentIfRowIsExpandable(row)}
                            </Fragment>
                        ))}
                    </TableBody>
                </TableElement>
            </TableContainer>

            {state.pagination.show && Number.isFinite(state.pagination.pageSize) && pageCount > 1 ? (
                <TablePagination size={pageCount} totalCount={amountOfRows} />
            ) : null}

            {renderEmpty()}

            {isLoading ? (
                <LoadingOverlay show={isLoading} data-test-id="table-loading-state">
                    <Spinner />
                </LoadingOverlay>
            ) : null}
            <PrintStyles enableRowSelection={rowSelectionEnabled} />
        </TableWrapper>
    );
};

interface StandaloneTableProps<T, F extends TableFilters<T> = TableFilters<T>> extends TableProps<T> {
    columns: ColumnDef<T, any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
    state: TableState<T, F>;
    onStateChange: (state: TableState<T, F>) => void;
}

export const StandaloneTable = <T, F extends TableFilters<T>>(props: StandaloneTableProps<T, F>) => (
    <TableProvider columns={props.columns} state={props.state} onStateChange={props.onStateChange}>
        <Table {...props} />
    </TableProvider>
);
